import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, Minus, RotateCcw, Eye, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useGuard } from "@/lib/guards";
import {
  ADMIN_SECTIONS, CONFIGURABLE_ROLES, ROLE_LABELS, DEFAULT_HIDDEN_SECTIONS, sectionAllowed, sectionStructural, requiredLevel,
  checkedFromHidden, planRoleSections, readRoleSectionsDraft, writeRoleSectionsDraft, clearRoleSectionsDraft, ROLE_SECTIONS_SAVED_EVENT,
} from "@/lib/adminSections";

// Lot 749 — « Sections visibles par rôle » : le super admin coche, rôle par rôle, les sections du menu admin affichées.
// Lot 750 — « Voir » sous chaque rôle : console du rôle avec les cases EN COURS (brouillon sessionStorage).
// Lot 758 — déplacée dans Utilisateurs › Gestion des rôles ; « coche et donne la permission d'un coup » : une case
// grisée se coche quand même, la permission minimale (Voir / Gérer) est accordée au rôle à Enregistrer, et les autres
// sections ouvertes par cette permission restent masquées tant qu'elles ne sont pas cochées (le rôle ne voit QUE la
// section accordée). Garde-fou `role_sections_one_click` (OFF → cases « — » comme avant).
const LEVEL_FR = { view: "Voir", manage: "Gérer" };

export const RoleSectionsCard = ({ onSaved, focus = false }) => {
  const navigate = useNavigate();
  const oneClick = useGuard("role_sections_one_click");
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(null);
  const [fromDraft, setFromDraft] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const draft = readRoleSectionsDraft();
    api.get("/admin/role-permissions")
      .then(({ data: d }) => {
        setData(d);
        setChecked(draft ? (draft.checked || checkedFromHidden(draft.hidden, d.roles)) : checkedFromHidden(d.hidden_sections || DEFAULT_HIDDEN_SECTIONS, d.roles));
        setFromDraft(!!draft);
      })
      .catch(() => { setData({ roles: {} }); setChecked(draft ? (draft.checked || {}) : checkedFromHidden(DEFAULT_HIDDEN_SECTIONS, {})); setFromDraft(!!draft); });
  }, []);
  useEffect(() => { if (focus && data && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "start" }); }, [focus, data]);
  const plan = useMemo(() => (checked && data ? planRoleSections(checked, data.roles || {}, oneClick) : null), [checked, data, oneClick]);
  if (!data || !checked || !plan) return <div className="yalola-card p-5 grid place-items-center" data-testid="settings-role-sections-loading"><Loader2 size={18} className="animate-spin" /></div>;
  const isOn = (role, key) => (checked[role] || []).includes(key);
  const toggle = (role, key) => setChecked((c) => ({ ...c, [role]: isOn(role, key) ? (c[role] || []).filter((k) => k !== key) : [...(c[role] || []), key] }));
  const save = async () => {
    setBusy(true);
    try {
      const body = { roles: plan.hidden, ...(Object.keys(plan.grants).length ? { grants: plan.grants } : {}) };
      const { data: d } = await api.patch("/admin/role-sections", body);
      const roles = d.roles || data.roles;
      const hidden = d.hidden_sections || plan.hidden;
      setData((x) => ({ ...x, roles, hidden_sections: hidden }));
      setChecked(checkedFromHidden(hidden, roles));
      clearRoleSectionsDraft(); setFromDraft(false);
      window.dispatchEvent(new CustomEvent(ROLE_SECTIONS_SAVED_EVENT, { detail: { hidden, ...(d.roles ? { roles: d.roles } : {}) } }));
      const granted = Object.entries(d.granted || {});
      toast.success(granted.length ? `Sections enregistrées — permissions accordées : ${granted.map(([r]) => ROLE_LABELS[r] || r).join(", ")}` : "Sections par rôle enregistrées");
      onSaved?.(d);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const reset = () => { setChecked(checkedFromHidden(DEFAULT_HIDDEN_SECTIONS, data.roles)); clearRoleSectionsDraft(); setFromDraft(false); };
  const preview = (role) => { writeRoleSectionsDraft(role, plan.hidden, plan.grants, checked); navigate(`/admin/dashboard?viewAs=${role}`); };
  const groups = [...new Set(ADMIN_SECTIONS.map((s) => s.group))];
  const labels = data.labels || {};
  const pendingGrants = Object.entries(plan.grants);
  return (
    <div className="yalola-card p-5" data-testid="settings-role-sections" ref={ref}>
      <div className="font-bold font-display text-lg mb-1">Sections visibles par rôle</div>
      <p className="text-sm mb-3" style={{ color: "var(--yalola-text-muted)" }}>
        Coche les sections du menu que chaque rôle voit dans sa console.
        {oneClick
          ? <> Une case <span className="inline-block h-3 w-3 rounded-sm border-2 border-dashed align-middle mx-0.5" style={{ borderColor: "#D97706" }} /> en pointillé signifie que la permission manque encore : la cocher l'accorde au rôle à l'enregistrement (niveau minimal : Voir, ou Gérer quand la section l'exige) — le rôle ne verra que cette section, pas les autres ouvertes par la même permission.</>
          : <> Une case grisée « — » signifie que les permissions du rôle (matrice ci-dessous) ne lui ouvrent pas cette section : donne d'abord la permission.</>}
        {" "}Les sections réservées au super admin ne sont pas listées. « Voir » ouvre la console du rôle avec les cases en cours, sans enregistrer.
      </p>
      {fromDraft && (
        <div className="rounded-lg px-3 py-2 mb-3 text-xs flex items-center gap-2" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }} data-testid="role-sections-draft-hint">
          <Eye size={13} /> Cases reprises de l'aperçu — pas encore enregistrées.
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="role-sections-table">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>
              <th className="py-2 pr-3 font-semibold">Section</th>
              {CONFIGURABLE_ROLES.map((r) => <th key={r} className="py-2 px-2 font-semibold text-center">{ROLE_LABELS[r]}</th>)}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <React.Fragment key={g}>
                <tr><td colSpan={CONFIGURABLE_ROLES.length + 1} className="pt-3 pb-1 text-[11px] font-bold" style={{ color: "var(--yalola-primary)" }}>{g}</td></tr>
                {ADMIN_SECTIONS.filter((s) => s.group === g).map((s) => (
                  <tr key={s.key} className="border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid={`role-section-row-${s.key}`}>
                    <td className="py-1.5 pr-3">{s.label}{s.rolesOnly ? <span className="ml-1 text-[10px]" style={{ color: "var(--yalola-text-muted)" }}>({s.rolesOnly.map((r) => ROLE_LABELS[r]).join(", ")})</span> : null}</td>
                    {CONFIGURABLE_ROLES.map((r) => {
                      const structural = sectionStructural(s, r);
                      const allowed = !structural && sectionAllowed(s, r, data.roles?.[r]);
                      const clickable = allowed || (oneClick && !structural);
                      const on = clickable && isOn(r, s.key);
                      const grant = !allowed && clickable ? `${labels[s.perm] || s.perm} (${LEVEL_FR[requiredLevel(s)]})` : "";
                      return (
                        <td key={r} className="py-1.5 px-2 text-center">
                          {clickable ? (
                            <button type="button" role="checkbox" aria-checked={on} onClick={() => toggle(r, s.key)}
                                    title={grant ? `Cocher accorde « ${grant} » au rôle ${ROLE_LABELS[r]} à l'enregistrement` : undefined}
                                    className={`h-6 w-6 rounded-md border-2 inline-grid place-items-center align-middle relative${grant ? " border-dashed" : ""}`}
                                    style={{ borderColor: on ? (grant ? "#D97706" : "var(--yalola-primary)") : (grant ? "#D97706" : "var(--yalola-border)"), background: on ? (grant ? "#D97706" : "var(--yalola-primary)") : "transparent" }}
                                    data-testid={`role-section-${r}-${s.key}`} data-state={on ? "on" : "off"} data-grant={grant ? `${s.perm}:${requiredLevel(s)}` : undefined}>
                              {on && <Check size={14} color="#fff" strokeWidth={3} />}
                              {on && grant && <span className="absolute -top-1.5 -right-1.5 h-3.5 w-3.5 rounded-full grid place-items-center" style={{ background: "#fff", color: "#D97706", boxShadow: "0 0 0 1px #D97706" }}><Plus size={9} strokeWidth={3} /></span>}
                            </button>
                          ) : (
                            <span className="inline-grid place-items-center h-6 w-6" title={structural ? "Section inexistante pour ce rôle" : "Non ouvert par les permissions du rôle"} style={{ color: "var(--yalola-text-muted)" }} data-testid={`role-section-${r}-${s.key}-na`}><Minus size={14} /></span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t" style={{ borderColor: "var(--yalola-border)" }} data-testid="role-sections-preview-row">
              <td className="pt-3 pr-3 text-[11px] font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Voir en tant que (cases en cours)</td>
              {CONFIGURABLE_ROLES.map((r) => (
                <td key={r} className="pt-3 px-2 text-center">
                  <button type="button" onClick={() => preview(r)} title={`Voir en tant que ${ROLE_LABELS[r]}`}
                          className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[11px] font-semibold transition-colors hover:bg-[color:var(--yalola-surface-alt)]"
                          style={{ borderColor: "var(--yalola-border)", color: "var(--yalola-primary)" }} data-testid={`role-section-preview-${r}`}>
                    <Eye size={12} /> Voir
                  </button>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {pendingGrants.length > 0 && (
        <div className="rounded-lg px-3 py-2 mt-3 text-xs space-y-0.5" style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="role-sections-grants">
          <div className="font-semibold">À l'enregistrement, ces permissions seront accordées :</div>
          {pendingGrants.map(([r, g]) => (
            <div key={r} data-testid={`role-sections-grants-${r}`}><b>{ROLE_LABELS[r] || r}</b> → {Object.entries(g).map(([cap, lvl]) => `${labels[cap] || cap} (${LEVEL_FR[lvl]})`).join(" · ")}</div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-4">
        <Button onClick={save} disabled={busy} className="rounded-full" data-testid="role-sections-save">{busy ? <Loader2 size={14} className="animate-spin mr-1" /> : null}Enregistrer</Button>
        <Button variant="outline" onClick={reset} disabled={busy} className="rounded-full" data-testid="role-sections-reset"><RotateCcw size={14} className="mr-1" />Valeurs par défaut</Button>
      </div>
    </div>
  );
};
